import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Grid,
  ListItemText,
  makeStyles,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import { ExpandMoreRounded, HelpOutlineRounded } from '@material-ui/icons';
import { Form, Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';
import React, { useState } from 'react';
import AppearanceSettings from './appearance-component';
import { SnackbarComponent } from '../../../../../core/utils/components/snackbar-component';
import { editTrackingSettings } from '../../../data/datasources/edit-tracking-settings';
import { refreshMeAction } from '../../../../me/presentation/redux/actions/me-actions';
import { useDispatch, useSelector } from 'react-redux';
import { SETTINGS_LOAD_SUCCESS } from '../../redux/types/settings-types';
import { ITrackingSettings, TrackingState } from '../../../domain/entities/settings-entity';
import { isEqual } from 'lodash';

export type TrackingSettingsFormValues = ITrackingSettings;

const useStyles = makeStyles((theme) => ({
  root: {
    margin: theme.spacing(0),
    width: '100%',
    backgroundColor: theme.palette.background.paper
  },
  margin: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  paper: {
    padding: theme.spacing(2),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    cursor: 'pointer'
  },
  container: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  formButtons: {
    marginTop: theme.spacing(1)
  },
  heading: {
    margin: theme.spacing(2)
  },
  textField: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff'
  }
}));

function CustomMessagesForm() {
  const classes = useStyles();
  const { settings } = useSelector((state) => state.settings);
  const orderNearbyEnabled = settings?.order_nearby?.enabled ?? false;

  const { values, errors, touched, handleChange, handleBlur } =
    useFormikContext<TrackingSettingsFormValues>();

  const states: TrackingState[] = [
    'pending',
    'batched',
    'started',
    ...(orderNearbyEnabled ? ['nearby' as TrackingState] : []),
    'arrived',
    'completed',
    'abandoned',
    'cancelled',
    'returned'
  ];

  const formikProps = (state) => ({
    name: `custom_messages.${state}`,
    value: values.custom_messages[state] ?? '',
    onChange: handleChange,
    onBlur: handleBlur,
    error: touched.custom_messages?.[state] && Boolean(errors.custom_messages?.[state]),
    helperText: touched.custom_messages?.[state] ? errors.custom_messages?.[state] : ''
  });

  return (
    <>
      <Typography variant="subtitle1">Custom Messages</Typography>
      <ListItemText secondary="Customise the messages your customers would see on the tracking page at each stage of the delivery" />

      {states.map((state) => (
        <Box
          key={state}
          display="flex"
          flexDirection="column"
          style={{ gap: '0px', fontWeight: 'bold', paddingBlock: '4px' }}
          alignItems="start"
        >
          <TextField
            {...formikProps(state)}
            label={state.charAt(0).toUpperCase() + state.slice(1)}
            variant="outlined"
            fullWidth
            className={classes.textField}
          />
        </Box>
      ))}
    </>
  );
}

export const TrackingSettings = ({ settings, openBackdrop, closeBackdrop, showSnackbar }) => {
  const dispatch = useDispatch();
  const classes = useStyles();

  const { custom_messages, appearance } = settings || {};

  const defaultAppearance = {
    font: 'Montserrat',
    background: '#ffffff',
    text_primary: '#000000',
    text_secondary: '#666666',
    text_accent: '#2b7fff'
  };
  const initialValues: TrackingSettingsFormValues = {
    custom_messages: {
      pending: custom_messages?.pending || 'Preparing order',
      batched: custom_messages?.batched || 'Order prepared',
      started: custom_messages?.started || 'Dispatched / Out of delivery',
      nearby: custom_messages?.nearby || 'Your order is close by',
      arrived: custom_messages?.arrived || 'Driver arrived at your location',
      completed: custom_messages?.completed || 'Order delivered',
      abandoned: custom_messages?.abandoned || 'Order was abandoned',
      cancelled: custom_messages?.cancelled || 'Order was cancelled',
      returned: custom_messages?.returned || 'Order was returned'
    },
    appearance: {
      font: appearance?.font || defaultAppearance.font,
      background: appearance?.background || defaultAppearance.background,
      text_primary: appearance?.text_primary || defaultAppearance.text_primary,
      text_secondary: appearance?.text_secondary || defaultAppearance.text_secondary,
      text_accent: appearance?.text_accent || defaultAppearance.text_accent
    }
  };

  const submit = async (data, actions) => {
    const { resetForm } = actions;
    const props = { resetForm, closeBackdrop, showSnackbar };

    try {
      openBackdrop();
      const result = await editTrackingSettings(data);
      dispatch({ type: SETTINGS_LOAD_SUCCESS, payload: result });

      props.showSnackbar('success', 'Tracking settings updated successfully');
    } catch (error) {
      if (error instanceof Error) {
        props.showSnackbar('error', error.message);
      } else {
        props.showSnackbar('error', 'Failed to update tracking settings');
      }
      return;
    } finally {
      props.closeBackdrop();
      dispatch(refreshMeAction());
    }
  };

  const validationSchema = Yup.object({
    custom_messages: Yup.object().shape(
      Object.fromEntries(
        Object.keys(initialValues.custom_messages).map((state) => [
          state,
          Yup.string().required(`Message for ${state} is required`)
        ])
      )
    )
  });

  return (
    <Accordion defaultExpanded>
      <AccordionSummary
        aria-controls="tracking-settings-content"
        id="tracking-settings-header"
        expandIcon={<ExpandMoreRounded />}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="subtitle1">Tracking Page Settings</Typography>
          <Tooltip
            placement="top"
            title="Customize the appearance and behavior of the tracking page."
          >
            <HelpOutlineRounded fontSize="small" style={{ marginLeft: 6 }} />
          </Tooltip>
        </div>
      </AccordionSummary>

      <AccordionDetails>
        <Formik
          initialValues={initialValues}
          onSubmit={submit}
          validationSchema={validationSchema}
          validateOnMount={true}
        >
          {({ handleSubmit, values, setFieldValue }) => (
            <Form onSubmit={handleSubmit} style={{ width: '100%' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6} lg={6}>
                  <CustomMessagesForm />
                </Grid>
                <Grid item xs={12} md={6} lg={6}>
                  <AppearanceSettings
                    reset={() => setFieldValue('appearance', defaultAppearance)}
                  />
                </Grid>
              </Grid>
              <div className={classes.formButtons}>
                <Button
                  className={classes.margin}
                  disabled={isEqual(values, { appearance, custom_messages })}
                  type="submit"
                  variant="contained"
                  size="large"
                  color="primary"
                  fullWidth
                >
                  Save Changes
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </AccordionDetails>
    </Accordion>
  );
};

export const TrackingSettingsComponent = (props) => {
  const classes = useStyles();
  const [backdrop, setBackdrop] = useState(false);
  const [snackbar, setSnackbar] = useState(false);
  const [message, setMessage] = useState({
    status: 'success',
    description: 'Updated'
  });

  const showSnackbar = (status = 'success', description = 'Updated') => {
    setSnackbar(true);
    setMessage({
      status,
      description
    });
    setBackdrop(false);
  };

  const closeSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setSnackbar(false);
  };

  const closeBackdrop = () => {
    setBackdrop(false);
  };

  const openBackdrop = () => {
    setBackdrop(true);
  };
  return (
    <div className={classes.root}>
      <TrackingSettings
        settings={props.settings}
        openBackdrop={openBackdrop}
        closeBackdrop={closeBackdrop}
        showSnackbar={showSnackbar}
      />

      <>
        <Backdrop
          className={classes.backdrop}
          open={backdrop}
          invisible={false}
          onClick={closeBackdrop}
        >
          <CircularProgress color="inherit" />
        </Backdrop>

        <SnackbarComponent
          open={snackbar}
          onClose={closeSnackbar}
          severity={message.status}
          message={message.description}
        />
      </>
    </div>
  );
};
